import React from "react";
import { useTranslation } from "react-i18next";
import { Check, Circle } from "lucide-react";

/** Google's "G", as both auth pages drew it inline before 2026-10-28. */
export function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

/**
 * The password rule, CLAUDE.md §5, as four separate checks.
 *
 * The same rule `models.common.validate_password_strength` enforces on the
 * server; split so the form can tick each one off while the person types,
 * instead of refusing the whole thing on submit with one long sentence.
 */
export const PASSWORD_CHECKS = [
  { key: "pwLength", test: (v) => v.length >= 8 },
  { key: "pwUpper", test: (v) => /[A-Z]/.test(v) },
  { key: "pwLower", test: (v) => /[a-z]/.test(v) },
  { key: "pwDigit", test: (v) => /\d/.test(v) },
];

export const passwordOk = (v) => PASSWORD_CHECKS.every((c) => c.test(v || ""));

export function PasswordRules({ value }) {
  const { t } = useTranslation();
  return (
    <div className="mt-2" data-testid="password-rules" aria-live="polite">
      <p className="text-xs text-smoke">{t("signupX.pwRulesTitle")}</p>
      <ul className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1">
        {PASSWORD_CHECKS.map((c) => {
          const ok = c.test(value || "");
          return (
            <li key={c.key}
                className={`flex items-center gap-1.5 text-sm ${ok ? "text-success" : "text-smoke"}`}
                data-testid={`password-rule-${c.key}`} data-ok={ok}>
              {ok ? <Check size={14} strokeWidth={3} aria-hidden="true" /> : <Circle size={10} aria-hidden="true" />}
              {t(`signupX.${c.key}`)}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function FieldError({ children, testid }) {
  if (!children) return null;
  return <p className="mt-1 text-sm text-danger" role="alert" data-testid={testid}>{children}</p>;
}

export const zipOk = (v) => /^\d{5}$/.test(String(v || "").trim());

/** A US number someone typed, as the string the server parses. */
export function phoneForServer(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 10) return `+1 ${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return String(raw || "").trim();
}

export const phoneOk = (v) => {
  const digits = String(v || "").replace(/\D/g, "");
  return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
};

export const emailOk = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v || "").trim());
